if (!customElements.get('drdog-bundles')) {
  customElements.define(
    'drdog-bundles',
    class DrdogBundles extends HTMLElement {
      connectedCallback() {
        this._onClick = this.onClick.bind(this);
        this.addEventListener('click', this._onClick);
        this.mountInlineUpsells();
      }

      /**
       * Move every <aside.drdog-inline-upsell> from inside this section into
       * a horizontal group attached above/below the buy form. Inline upsells
       * with the same data-mount-id share a group so two of them sit
       * side-by-side on the Dry shampoo PDP.
       */
      mountInlineUpsells() {
        const upsells = Array.from(this.querySelectorAll('aside.drdog-inline-upsell'));
        if (upsells.length === 0) return;

        const buyForm =
          document.querySelector('product-form form') ||
          document.querySelector('form[action$="/cart/add"]');
        if (!buyForm) return;

        const groups = new Map(); // groupKey -> { position, elements[] }
        for (const u of upsells) {
          const groupId = u.dataset.mountId || 'default';
          const position = u.dataset.mountPosition === 'before' ? 'before' : 'after';
          const key = `${groupId}::${position}`;
          if (!groups.has(key)) groups.set(key, { position, elements: [] });
          groups.get(key).elements.push(u);
        }

        for (const { position, elements } of groups.values()) {
          if (elements.length === 0) continue;
          const wrapper = document.createElement('div');
          wrapper.className =
            'drdog-inline-upsell-group' +
            (elements.length > 1 ? ' drdog-inline-upsell-group--multi' : '');
          // Inline upsells leave the <drdog-bundles> subtree, so click events
          // from their buttons no longer bubble to the host's listener.
          // Re-attach the same delegated handler on the wrapper.
          wrapper.addEventListener('click', this._onClick);
          for (const el of elements) {
            el.removeAttribute('hidden');
            wrapper.appendChild(el);
          }
          if (position === 'before') {
            buyForm.insertAdjacentElement('beforebegin', wrapper);
          } else {
            buyForm.insertAdjacentElement('afterend', wrapper);
          }
        }
      }

      async onClick(evt) {
        const btn = evt.target.closest('[data-bundle-add]');
        if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
        evt.preventDefault();

        // Inline upsells get moved out of <drdog-bundles> at mount time, so
        // their click events fire from outside the custom element. Run the
        // shared add-to-cart logic regardless of where the button lives now.
        await this.addBundleToCart(btn);
      }

      async addBundleToCart(btn) {
        const ids = (btn.dataset.variants || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (btn.dataset.includeCurrentProduct === 'true') {
          const mainId = this.readCurrentProductVariantId();
          if (mainId && !ids.includes(mainId)) {
            ids.unshift(mainId);
          }
        }

        if (ids.length === 0) return;

        const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
        const cart =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const errEl = btn.parentElement
          ? btn.parentElement.querySelector('.drdog-bundle__error, .drdog-inline-upsell__error') ||
            btn.closest('.drdog-inline-upsell')?.querySelector('.drdog-inline-upsell__error')
          : null;

        if (errEl) {
          errEl.hidden = true;
          errEl.textContent = '';
        }

        btn.classList.add('is-loading');
        btn.setAttribute('aria-disabled', 'true');

        try {
          const sectionsToRender =
            cart && typeof cart.getSectionsToRender === 'function'
              ? cart.getSectionsToRender().map((s) => s.id)
              : [];

          if (cart && typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }

          const body = {
            items,
            sections: sectionsToRender.join(','),
            sections_url: window.location.pathname,
          };

          const cartAddUrl =
            (window.routes && window.routes.cart_add_url) || '/cart/add';

          const res = await fetch(`${cartAddUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });

          const data = await res.json();

          if (data.status) {
            throw new Error(data.description || data.message || 'Could not add to cart.');
          }

          const code = btn.dataset.discountCode;
          if (code) {
            fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(
              () => {}
            );
          }

          // Multi-item /cart/add.js returns { items: [...], sections } with no top-level
          // `key` or `id`. cart-notification renderContents() builds its target selector
          // from `parsedState.key` and would otherwise throw on a null querySelector.
          //
          // The cart-notification-product section iterates cart.items, so we pull a key
          // straight from the rendered HTML — that guarantees the selector matches
          // even when /cart/add.js item keys diverge from the section's cart state.
          const productSectionHtml = data.sections && data.sections['cart-notification-product'];
          if (productSectionHtml) {
            const matches = [
              ...String(productSectionHtml).matchAll(/id="cart-notification-product-([^"]+)"/g),
            ];
            if (matches.length > 0) {
              data.key = matches[matches.length - 1][1];
            }
          }
          if (Array.isArray(data.items) && data.items.length > 0) {
            const lastItem = data.items[data.items.length - 1];
            if (lastItem) {
              if (data.key == null) data.key = lastItem.key;
              if (data.id == null) data.id = lastItem.id;
            }
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (typeof cart.open === 'function') cart.open();
          } else {
            const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
            window.location = cartUrl;
            return;
          }

          if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'drdog-bundles',
              productVariantId: ids[0],
              cartData: data,
              bundleName: btn.dataset.bundleName || '',
            });
          }

          if (window.analytics && typeof window.analytics.trackAddToCart === 'function') {
            try {
              window.analytics.trackAddToCart(data, ids[0]);
            } catch (_) {
              /* analytics never blocks the cart */
            }
          }
        } catch (err) {
          console.error('[drdog-bundles]', err);
          if (errEl) {
            errEl.textContent =
              err && err.message ? err.message : 'Could not add. Please try again.';
            errEl.hidden = false;
          }
        } finally {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-disabled');
        }
      }

      readCurrentProductVariantId() {
        const selectors = [
          'product-info form[action*="/cart/add"] [name="id"]',
          'product-form-component form[action*="/cart/add"] [name="id"]',
          'product-form form[action*="/cart/add"] [name="id"]',
          'main form[action*="/cart/add"] [name="id"]',
          'form[action*="/cart/add"] [name="id"]',
        ];
        for (const sel of selectors) {
          const input = document.querySelector(sel);
          const value = input && input.value && input.value.trim();
          if (value) return value;
        }
        return null;
      }
    }
  );
}
